// src/components/events/EventsImporter.jsx
// Steward Events Importer (Phase 4) — upload -> parse -> match -> review -> import (unpublished).
// Publishing happens later, from the separate Pending Events view (PendingEvents.jsx).
//
// Steward/admin-gated. Reached via ournet:navigate { view: 'events-importer' }.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsList from '../core/HeadersAndFooters/FooterButtonsList';
import StandardButton from '../core/StandardButton';
import { useTranslation, useFormattingLocale } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';
import { isPlatformAdmin } from '../../utils/adminHelpers';
import { isSignedIn } from '../../contexts/AuthContext';
import { getMyStewardships } from '../../data/directoryService';
import { eventService } from '../../data/dataService';
import { validateImageFile } from '../../utils/fileValidation';
import { processImage, uploadToSupabase } from '../core/ImageCropper/imageProcessing';
import { formatFreguesiaList, computeInstructionWindow } from '../../utils/aiPromptTemplates';
import { pickTagImage } from '../../utils/tagImageMatcher';
import {
  parseImportCsv,
  boundaryNameMatches,
  matchFreguesiaWithinGrant,
  findDuplicate,
  findDuplicatesWithinBatch
} from '../../utils/eventsImportParsing';
import {
  getStewardFreguesiaAreas,
  getBoundaryWithParentName,
  getExistingEventsForFreguesia,
  searchFreguesiaNationally,
  fetchEventImageViaEdgeFunction,
  importEvent,
  setEventImage,
  setEventTagSlugs,
  setEventTagImage
} from '../../data/eventsImportService';

// Question-by-question entry flow (docs/briefs/2026-08-11-simple-importer-design.md
// §3) — reuses the STEPS-array + numeric index + canAdvance() state/logic pattern
// from WalkingComposer.jsx (the only other stepper in src/), but NOT its chrome:
// WalkingComposer is a full-screen fixed-overlay takeover; this wizard lives
// inside EventsImporter's existing page (HeaderContainer/FooterContainer already
// wrap it), so the dot-indicator/back/next controls here are new, in-page, and
// unrelated to WalkingComposer's position:fixed overlay shell.
const WIZARD_STEPS = ['parish', 'posters', 'types', 'window', 'instruction', 'paste'];

// Centre-crop rectangle filling a 4:3 frame from natural dimensions. Duplicates
// WalkingComposer.jsx's inline computeAutoCrop — flagged as a reuse candidate in
// docs/reuse-backlog.md (a second consumer of the same maths), not extracted here.
function computeAutoCrop(naturalWidth, naturalHeight) {
  const targetAspect = 4 / 3;
  const sourceAspect = naturalWidth / naturalHeight;
  let cropW, cropH, cropX, cropY;
  if (sourceAspect > targetAspect) {
    cropH = naturalHeight;
    cropW = Math.round(naturalHeight * targetAspect);
    cropX = Math.round((naturalWidth - cropW) / 2);
    cropY = 0;
  } else {
    cropW = naturalWidth;
    cropH = Math.round(naturalWidth / targetAspect);
    cropX = 0;
    cropY = Math.round((naturalHeight - cropH) / 2);
  }
  return { x: cropX, y: cropY, width: cropW, height: cropH };
}

function loadImageDimensions(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(blob);
  });
}

// eventsImportService.js throws stable error codes (not English) so the UI can
// translate them — see that file's header comment. Anything not in this list
// (e.g. a raw fileValidation.js message) falls back to a generic translated
// string rather than ever rendering un-translated text.
// `heic_too_large` removed (Phase 5c §2) along with HEIC handling — see the
// removal note on processRowImage below.
const KNOWN_ERROR_CODES = new Set([
  'not_logged_in', 'image_fetch_failed', 'import_event_failed', 'image_attach_failed',
  // Why a fetch failed (docs/briefs/2026-10-02-importer-images-arrive-again.md, ruling 4) —
  // codes from fetchEventImageErrorCode() in eventsImportService.js.
  'image_fetch_bad_address', 'image_fetch_not_allowed', 'image_fetch_too_large',
  'image_fetch_rate_limited', 'image_fetch_unreachable'
]);

function translateErrorCode(t, code) {
  return t(KNOWN_ERROR_CODES.has(code) ? `error_${code}` : 'error_import_row_failed');
}

// ---------------------------------------------------------------------------
// Per-row freguesia routing (Phase 5b §2) — resolves ONE row's locality/
// municipality against the steward's granted freguesia set. Replaces the old
// single-freguesia mismatch check entirely (see eventsImportParsing.js header
// comment on boundaryNameMatches for why). Resolution rules, in order:
//   1. blank locality           -> the batch-picked freguesia, unflagged
//   2. exactly one grant match, confirmed by municipality if stated
//                                -> that freguesia, unflagged
//   3. >1 grant match           -> disambiguate via municipality if given,
//                                   else flag ambiguous (hard-blocked — a
//                                   human decides, never pick the first)
//   4. 0 grant matches, but a real freguesia exists nationally by that name
//                                -> flag outside-grant (hard-blocked, never
//                                   importable by override — an authorisation
//                                   boundary, not a warning)
//   5. 0 grant matches, no national match either
//                                -> flag unresolved (opt-in fallback to the
//                                   batch freguesia, unticked by default)
//
// Municipality is checked whenever the row states one — not only to break a
// tie among multiple grant matches, but also to CONFIRM a single match (brief
// §2.3 row 1 names "locality + municipality" as the pair that resolves; §2.4:
// "as a pair is what makes this safe nationally — parish names repeat across
// concelhos"). A single locality-only match whose stated municipality
// disagrees with that freguesia's actual concelho is not trusted — it likely
// names a same-named freguesia in a DIFFERENT concelho that happens not to be
// in the grant, exactly the case rule 4 exists to hard-block. Falls through to
// national resolution rather than silently importing on a locality-only match.
// ---------------------------------------------------------------------------
async function resolveRowRouting(row, grantedFreguesias, batchFreguesiaId, batchFreguesiaName) {
  const locality = row.localityRaw;
  if (!locality) {
    return { resolvedFreguesiaId: Number(batchFreguesiaId), resolvedFreguesiaName: batchFreguesiaName, routingFlag: null };
  }

  let candidates = matchFreguesiaWithinGrant(locality, grantedFreguesias);

  if (candidates.length >= 1 && row.municipalityRaw) {
    const withParent = await Promise.all(candidates.map(async c => {
      const { parentName } = await getBoundaryWithParentName(c.boundary_id);
      return { ...c, parentName };
    }));
    candidates = withParent.filter(c => c.parentName && boundaryNameMatches(row.municipalityRaw, c.parentName));
  }

  if (candidates.length === 1) {
    return { resolvedFreguesiaId: candidates[0].boundary_id, resolvedFreguesiaName: candidates[0].boundary_name, routingFlag: null };
  }
  if (candidates.length > 1) {
    return {
      resolvedFreguesiaId: null, resolvedFreguesiaName: null,
      routingFlag: { type: 'routing_ambiguous', messageKey: 'flag_routing_ambiguous', messageParams: { value: locality } }
    };
  }

  // No within-grant match — check nationally to distinguish "real parish,
  // just not yours" (hard-blocked) from "doesn't resolve at all" (opt-in
  // fallback). See eventsImportService.js's searchFreguesiaNationally header
  // comment for the accent-sensitivity caveat on this national search.
  const national = await searchFreguesiaNationally(locality);
  if (national.length > 0) {
    return {
      resolvedFreguesiaId: null, resolvedFreguesiaName: null,
      routingFlag: { type: 'routing_outside_grant', messageKey: 'flag_routing_outside_grant', messageParams: { value: locality } }
    };
  }
  return {
    resolvedFreguesiaId: null, resolvedFreguesiaName: null,
    routingFlag: { type: 'routing_unresolved', messageKey: 'flag_routing_unresolved', messageParams: { value: locality } }
  };
}

const EventsImporter = ({ currentUser, onBack, currentContext }) => {
  const { t, locale } = useTranslation('importer');

  const [authorised, setAuthorised] = useState(null); // null = checking, true/false once known
  const [freguesias, setFreguesias] = useState([]);
  const [selectedFreguesiaId, setSelectedFreguesiaId] = useState('');
  const [pastedCsvText, setPastedCsvText] = useState('');
  const [copyStatus, setCopyStatus] = useState(null); // null | 'success' | 'error'

  // Question-by-question entry-flow state (design §3/§4). wizardStep indexes
  // WIZARD_STEPS; the other four hold each question's answer.
  const [wizardStep, setWizardStep] = useState(0);
  const [postersAnswer, setPostersAnswer] = useState(null); // null (unanswered) | true | false
  const [eventTypesText, setEventTypesText] = useState(''); // optional free text
  const [monthsAhead, setMonthsAhead] = useState(1); // 1 | 2 | 3
  const [tagVocabulary, setTagVocabulary] = useState([]); // [{id, terms:[{id,...}]}] — for the tag block + the tag_slugs follow-up write
  const [houseFallbackUrl, setHouseFallbackUrl] = useState(null); // image-layer build — R4 fallback for a row whose tags have no matched image

  const [step, setStep] = useState('upload'); // upload | review | importing | done
  const [rows, setRows] = useState([]);
  const [rowCounts, setRowCounts] = useState({ read: 0, shown: 0 }); // Phase 5c §5a
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);

  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [importResults, setImportResults] = useState({ succeeded: 0, failed: [] });

  // --- Steward/admin gate ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSignedIn(currentUser)) { setAuthorised(false); return; }
      if (isPlatformAdmin(currentUser)) { if (!cancelled) setAuthorised(true); return; }
      const stewardships = await getMyStewardships();
      if (!cancelled) setAuthorised(stewardships.length > 0);
    })();
    return () => { cancelled = true; };
  }, [currentUser]);

  useEffect(() => {
    if (authorised !== true) return;
    getStewardFreguesiaAreas().then(setFreguesias);
  }, [authorised]);

  // Ratified tag vocabulary, once on mount — reuses eventService.getTagVocabulary()
  // (added in the 11 Aug tag-picker build), the same reader AddEditEvent.jsx uses.
  // Feeds both the compiled instruction's tag block and the import-time filter
  // that keeps the tag_slugs follow-up write to valid slugs only.
  useEffect(() => {
    if (authorised !== true) return;
    let cancelled = false;
    eventService.getTagVocabulary().then(vocab => {
      if (!cancelled) setTagVocabulary(vocab);
    });
    return () => { cancelled = true; };
  }, [authorised]);

  // R4 house fallback URL (image-layer build) — fetched once, used by the
  // tag-image matcher follow-up below when none of a row's tags has a
  // matched illustration yet.
  useEffect(() => {
    if (authorised !== true) return;
    let cancelled = false;
    eventService.getTagHouseFallbackImageUrl().then(url => {
      if (!cancelled) setHouseFallbackUrl(url);
    });
    return () => { cancelled = true; };
  }, [authorised]);

  const ratifiedSlugSet = useMemo(
    () => new Set(tagVocabulary.flatMap(category => category.terms.map(term => term.id))),
    [tagVocabulary]
  );

  // Written as a list, not a single name — the picker is single-select in this
  // phase, but the instruction panel's text generation must tolerate a set
  // (Phase 5a Addendum 1 §4; Phase 5b makes the batch itself multi-freguesia).
  const selectedFreguesiaNames = useMemo(() => {
    const match = freguesias.find(f => String(f.boundary_id) === String(selectedFreguesiaId));
    return match ? [match.boundary_name] : [];
  }, [freguesias, selectedFreguesiaId]);

  // Compiled instruction (design §4, build brief §3). Assembly order — stated on
  // the record per the brief's request: [conditional poster block] + the existing
  // UNCHANGED ai_instructions_body (parish + CSV spec + rules — one opaque
  // translated string, no internal seam per the 11 Aug state probe) + an appended
  // data section (event-types/date-window sentence, then the tag block). The
  // wrapping sentences (poster block, types/window intro, tag block) are each
  // their own translated t() key with interpolation slots — per Librarian Brief
  // mode, TRANSLATION.md's no-hardcoded-English rule is unqualified and has no
  // AI-facing exemption, since the steward reads this box too. Only the bare
  // values (the slug list, the locale-formatted dates, the steward's own typed
  // event-types text) are un-translated data.
  const aiPromptText = useMemo(() => {
    if (selectedFreguesiaNames.length === 0) return '';
    const parish = formatFreguesiaList(selectedFreguesiaNames, locale);
    const base = t('ai_instructions_body', { parish });

    const posterBlock = postersAnswer === true ? t('data_poster_block') : '';

    const typesClause = eventTypesText.trim() || t('data_types_all_fallback');
    const { startText, endText } = computeInstructionWindow(monthsAhead, locale);
    const typesWindowIntro = t('data_types_window_intro', {
      types_clause: typesClause,
      window_start: startText,
      window_end: endText
    });

    const slugList = tagVocabulary
      .flatMap(category => category.terms.map(term => term.id))
      .join(', ');
    const tagBlock = t('data_tag_block', { tag_slug_list: slugList });

    return [posterBlock, base, typesWindowIntro, tagBlock].filter(Boolean).join('\n\n');
  }, [selectedFreguesiaNames, locale, t, postersAnswer, eventTypesText, monthsAhead, tagVocabulary]);

  const canAdvanceWizard = () => {
    switch (WIZARD_STEPS[wizardStep]) {
      case 'parish':
        return !!selectedFreguesiaId;
      case 'posters':
        return postersAnswer !== null;
      case 'window':
        return !!monthsAhead;
      default:
        return true; // 'types' is optional; 'instruction' has nothing to fill in
    }
  };

  const goWizardNext = () => {
    if (wizardStep < WIZARD_STEPS.length - 1) setWizardStep(wizardStep + 1);
  };

  const goWizardBack = () => {
    if (wizardStep > 0) setWizardStep(wizardStep - 1);
  };

  const handleCopyInstructions = () => {
    navigator.clipboard.writeText(aiPromptText)
      .then(() => setCopyStatus('success'))
      .catch(err => {
        console.error('Failed to copy instructions:', err);
        setCopyStatus('error');
      });
  };

  useEffect(() => {
    if (!copyStatus) return;
    const timer = setTimeout(() => setCopyStatus(null), 3000);
    return () => clearTimeout(timer);
  }, [copyStatus]);

  const handleParse = useCallback(async () => {
    if (!pastedCsvText.trim() || !selectedFreguesiaId) return;
    setParsing(true);
    setParseError(null);
    try {
      const { rows: matchedRows, columnsOk, rawRowCount } = parseImportCsv(pastedCsvText);
      if (!columnsOk) {
        setParseError(t('error_column_mismatch'));
        setParsing(false);
        return;
      }

      const { name: freguesiaName } = await getBoundaryWithParentName(Number(selectedFreguesiaId));

      // Per-row freguesia routing (Phase 5b §2) — resolved in parallel, one
      // async pass per row (fan-out is bounded by batch size). A blank-locality
      // row needs no RPC; a stated locality with municipality blank resolves
      // from the already-loaded granted set, no RPC either. A stated
      // municipality does cost a getBoundaryWithParentName() call per grant
      // candidate to confirm the pair (§2.3/§2.4) — uncached across rows that
      // share a boundary; a reuse candidate for larger batches, not built here.
      const routings = await Promise.all(
        matchedRows.map(row => resolveRowRouting(row, freguesias, selectedFreguesiaId, freguesiaName))
      );

      // Existing-events fetch is now per DISTINCT resolved freguesia, not just
      // the batch pick — a multi-parish batch's duplicate check must compare
      // each row against the freguesia it actually routes to (Phase 5b §2
      // consequence: the old single-freguesia fetch would silently miss
      // duplicates in any freguesia other than the one picked in the dropdown).
      const distinctFreguesiaIds = [...new Set(routings.map(r => r.resolvedFreguesiaId).filter(Boolean))];
      if (!distinctFreguesiaIds.includes(Number(selectedFreguesiaId))) {
        distinctFreguesiaIds.push(Number(selectedFreguesiaId));
      }
      const existingEventsEntries = await Promise.all(
        distinctFreguesiaIds.map(async id => [id, await getExistingEventsForFreguesia(id)])
      );
      const existingEventsByFreguesia = new Map(existingEventsEntries);

      // Batch-internal duplicates (Phase 5b §6.1) — two rows in THIS parse
      // sharing a title+date, e.g. a steward re-running last month's import.
      // findDuplicate() alone never catches this: it only ever compares a row
      // against already-imported DB rows.
      const duplicatesInBatch = findDuplicatesWithinBatch(matchedRows);

      const enriched = matchedRows.map((row, idx) => {
        const flags = [...row.flags];
        const routing = routings[idx];
        if (routing.routingFlag) flags.push(routing.routingFlag);

        if (duplicatesInBatch.has(row.rowIndex)) {
          flags.push({ type: 'duplicate_in_batch', messageKey: 'flag_duplicate_in_batch', messageParams: { title: row.title } });
        } else {
          const existingEvents = existingEventsByFreguesia.get(routing.resolvedFreguesiaId) || [];
          const duplicate = findDuplicate(row, existingEvents);
          if (duplicate) flags.push({ type: 'duplicate', messageKey: 'flag_duplicate', messageParams: { title: duplicate.title } });
        }

        // Hard-blocked: mechanically excluded, no override checkbox at all.
        // routing_outside_grant / routing_ambiguous join the existing set —
        // an authorisation boundary and a human-decides case, neither is a
        // warning a steward can tick through (brief §2.3).
        const blocked = flags.some(f =>
          f.type === 'missing_required' || f.type === 'ambiguous_date' ||
          f.type === 'routing_outside_grant' || f.type === 'routing_ambiguous'
        );
        // Any flagged-but-not-blocked row (routing unresolved, duplicate,
        // duplicate-in-batch, ambiguous time, invalid max_attendees) must
        // start UNTICKED — the steward opts in, never pre-agrees (brief §3.7,
        // carried forward from Phase 5a).
        return {
          ...row,
          flags,
          blocked,
          resolvedFreguesiaId: routing.resolvedFreguesiaId,
          resolvedFreguesiaName: routing.resolvedFreguesiaName,
          includeInImport: !blocked && flags.length === 0,
          rightsConfirmed: false
        };
      });

      setRows(enriched);
      setRowCounts({ read: rawRowCount, shown: enriched.length });
      setStep('review');
    } catch (err) {
      // err.message here can be a raw runtime/DOMException string (e.g. a
      // network failure or a file-read error) — never render it directly,
      // per TRANSLATION.md's no-hardcoded-English rule. Log the real detail,
      // always show the translated generic message.
      console.error('Events importer parse error:', err);
      setParseError(t('error_parse_failed'));
    } finally {
      setParsing(false);
    }
  }, [pastedCsvText, selectedFreguesiaId, freguesias, t]);

  const handleToggleInclude = (rowIndex) => {
    setRows(prev => prev.map(r => r.rowIndex === rowIndex ? { ...r, includeInImport: !r.includeInImport } : r));
  };

  const handleToggleRights = (rowIndex) => {
    setRows(prev => prev.map(r => r.rowIndex === rowIndex ? { ...r, rightsConfirmed: !r.rightsConfirmed } : r));
  };

  // The only remaining image source is image_url, fetched server-side (Phase 5c
  // §2 removed the photo picker — that capability moved to MyArticles.jsx,
  // per-event and visual, no filename matching). HEIC handling removed with
  // it: fetch-event-image's own MIME allow-list (jpeg/png/gif/webp/bmp) and
  // forced Content-Type header mean a fetched blob can never report as HEIC,
  // and a Blob has no .name for the filename-suffix check either — both arms
  // of the old looksLikeHeic() check were unreachable through this path alone,
  // confirmed dead, not merely rare.
  const processRowImage = async (row) => {
    if (!row.imageUrl || !row.rightsConfirmed) return null; // no image for this row
    const sourceBlob = await fetchEventImageViaEdgeFunction(row.imageUrl);

    const validation = await validateImageFile(sourceBlob);
    if (!validation.valid) throw new Error(validation.error);

    const dims = await loadImageDimensions(sourceBlob);
    const cropArea = computeAutoCrop(dims.width, dims.height);
    const { displayBlob, thumbnailBlob, fileName } = await processImage(sourceBlob, cropArea, '4:3');
    return uploadToSupabase(displayBlob, thumbnailBlob, fileName, 'hero-images', currentUser.id);
  };

  const handleImport = async () => {
    const toImport = rows.filter(r => r.includeInImport);
    setStep('importing');
    setImportProgress({ done: 0, total: toImport.length });

    let succeeded = 0;
    const failed = [];

    for (const row of toImport) {
      try {
        const image = await processRowImage(row);

        // resolvedFreguesiaId is null only for an opted-in "routing_unresolved"
        // row (Phase 5b §2.3's opt-in fallback) — every other included row
        // already carries its own resolved destination, per-row (§2 is the
        // whole point of this phase; a row must never import to the batch
        // picker's freguesia just because that's what was in scope before).
        const newId = await importEvent({
          authorId: currentUser.id,
          freguesiaId: row.resolvedFreguesiaId ?? Number(selectedFreguesiaId),
          title: row.title,
          description: row.description,
          startDate: row.startDateTime ? row.startDateTime.toISOString() : null,
          summary: row.summary,
          endDate: row.endDateTime ? row.endDateTime.toISOString() : null,
          isAllDay: row.isAllDay,
          venueName: row.venueName,
          venueAddress: row.venueAddress,
          venuePostcode: row.venuePostcode,
          priceInfo: row.priceInfo,
          requiresRsvp: row.requiresRsvp,
          maxAttendees: row.maxAttendees,
          contactEmail: row.contactEmail,
          contactPhone: row.contactPhone,
          eventUrl: row.eventUrl,
          tags: row.tags
        });

        if (image) {
          await setEventImage(newId, image);
        }

        // tag_slugs follow-up write (Addendum 1) — filter the row's parsed tags
        // to the live ratified vocabulary before writing, so the DB trigger never
        // has to reject anything on the happy path (Addendum 1 guardrail 1).
        // row.tags is a comma-joined string or null (parseTagsField in
        // eventsImportParsing.js), not an array. setEventTagSlugs() does not
        // throw on failure (guardrail 2) — caught here anyway, belt and braces,
        // so a tag-attach problem can never turn this already-succeeded import
        // into a reported failure.
        let validSlugs = [];
        try {
          const candidateSlugs = row.tags ? row.tags.split(',').map(s => s.trim()) : [];
          validSlugs = candidateSlugs.filter(slug => ratifiedSlugSet.has(slug));
          if (validSlugs.length > 0) {
            const { error: tagError } = await setEventTagSlugs(newId, validSlugs);
            if (tagError) console.error('setEventTagSlugs failed for row', row.rowIndex, tagError);
          }
        } catch (tagErr) {
          console.error('Exception attaching tag_slugs for row', row.rowIndex, tagErr);
        }

        // Tag-image matcher follow-up (image-layer build §4/§9). R5: never
        // overwrite a steward-supplied photo — `image` truthy means
        // processRowImage()/setEventImage() above already set cover_image_url
        // to that photo for this row, so skip entirely rather than read the
        // row back. Same non-throwing posture as the tag_slugs write above.
        if (!image) {
          try {
            const imageUrl = pickTagImage(validSlugs, tagVocabulary) || houseFallbackUrl;
            if (imageUrl) {
              const { error: imageMatchError } = await setEventTagImage(newId, imageUrl);
              if (imageMatchError) console.error('setEventTagImage failed for row', row.rowIndex, imageMatchError);
            }
          } catch (matchErr) {
            console.error('Exception matching tag image for row', row.rowIndex, matchErr);
          }
        }

        succeeded += 1;
      } catch (err) {
        console.error('Import failed for row', row.rowIndex, err);
        failed.push({ title: row.title, errorCode: err.message });
      }
      setImportProgress(prev => ({ ...prev, done: prev.done + 1 }));
    }

    setImportResults({ succeeded, failed });
    setStep('done');
  };

  const goToPendingEvents = () => {
    window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'events-pending' } }));
  };

  if (authorised === null) {
    return null;
  }

  if (authorised === false) {
    return (
      <>
        <HeaderContainer />
        <HeaderButtonsForm currentContext={currentContext} onBack={onBack} headerTitle={t('title')} />
        <div style={{ marginTop: 120, padding: 24, textAlign: 'center' }}>{t('error_not_authorised')}</div>
        <FooterContainer><FooterButtonsList showGoToTop={false} /></FooterContainer>
      </>
    );
  }

  return (
    <>
      <HeaderContainer />
      <HeaderButtonsForm currentContext={currentContext} onBack={onBack} headerTitle={t('title')} />
      <div style={{ marginTop: 120, marginBottom: 140, padding: '0 24px', maxWidth: 900, marginLeft: 'auto', marginRight: 'auto' }}>

        {step === 'upload' && (
          <div>
            <h2>{t('upload_heading')}</h2>

            {/* Step chrome — dot progress + back/next, in-page (not a full-screen
                overlay the way WalkingComposer's stepper is). Back is hidden on
                the first step: EventsImporter's own page-level Back
                (HeaderButtonsForm, above) already covers leaving the page from
                there, so the wizard doesn't need a second exit control. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              {wizardStep > 0 ? (
                <StandardButton variant="context" iconName="ArrowLeft" label={t('wizard_nav_back')} onClick={goWizardBack} />
              ) : <div />}

              <div style={{ display: 'flex', gap: 6 }}>
                {WIZARD_STEPS.map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: i === wizardStep
                        ? tokens.colors.action
                        : i < wizardStep
                          ? tokens.colors.actionDilute
                          : tokens.colors.borderSecondary
                    }}
                  />
                ))}
              </div>

              {WIZARD_STEPS[wizardStep] !== 'paste' ? (
                <StandardButton variant="action" iconName="ChevronRight" label={t('wizard_nav_next')} disabled={!canAdvanceWizard()} onClick={goWizardNext} />
              ) : <div />}
            </div>

            {WIZARD_STEPS[wizardStep] === 'parish' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{t('label_freguesia')}</label>
                <select
                  value={selectedFreguesiaId}
                  onChange={e => setSelectedFreguesiaId(e.target.value)}
                  style={{ width: '100%', padding: 8 }}
                >
                  <option value="">{t('placeholder_select_freguesia')}</option>
                  {freguesias.map(f => (
                    <option key={f.boundary_id} value={f.boundary_id}>{f.boundary_name}</option>
                  ))}
                </select>
              </div>
            )}

            {WIZARD_STEPS[wizardStep] === 'posters' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>{t('wizard_step_posters_question')}</label>
                <div style={{ display: 'flex', gap: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="radio" name="posters" checked={postersAnswer === true} onChange={() => setPostersAnswer(true)} style={{ accentColor: tokens.colors.action }} />
                    {t('wizard_posters_yes')}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="radio" name="posters" checked={postersAnswer === false} onChange={() => setPostersAnswer(false)} style={{ accentColor: tokens.colors.action }} />
                    {t('wizard_posters_no')}
                  </label>
                </div>
                {postersAnswer === true && (
                  <p style={{ fontSize: 13, color: tokens.colors.textSecondary, marginTop: 8 }}>{t('wizard_posters_helper')}</p>
                )}
              </div>
            )}

            {WIZARD_STEPS[wizardStep] === 'types' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{t('wizard_step_types_question')}</label>
                <input
                  type="text"
                  value={eventTypesText}
                  onChange={e => setEventTypesText(e.target.value)}
                  placeholder={t('wizard_types_placeholder')}
                  style={{ width: '100%', padding: 8, border: `1px solid ${tokens.colors.borderSecondary}`, borderRadius: 8, boxSizing: 'border-box' }}
                />
              </div>
            )}

            {WIZARD_STEPS[wizardStep] === 'window' && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>{t('wizard_step_window_question')}</label>
                <div style={{ display: 'flex', gap: 16 }}>
                  {[1, 2, 3].map(n => (
                    <label key={n} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="window" checked={monthsAhead === n} onChange={() => setMonthsAhead(n)} style={{ accentColor: tokens.colors.action }} />
                      {t(`wizard_window_${n}`)}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {WIZARD_STEPS[wizardStep] === 'instruction' && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 13, color: tokens.colors.textSecondary, marginBottom: 8 }}>{t('wizard_step_instruction_lead')}</p>
                {/* The AI prompt — alone inside the copy box, so one click copies
                    exactly what goes into the chat window and nothing else. */}
                <div style={{ padding: 12, border: `1px solid ${tokens.colors.borderSecondary}`, borderRadius: 8, background: '#F9FAFB' }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{t('instructions_heading')}</label>
                  <textarea
                    readOnly
                    value={aiPromptText}
                    style={{ width: '100%', minHeight: 220, padding: 8, fontSize: 12, boxSizing: 'border-box' }}
                  />
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <StandardButton variant="utility" iconName="Copy" label={t('button_copy_instructions')} onClick={handleCopyInstructions} />
                    {copyStatus === 'success' && <span style={{ color: '#059669', fontSize: 13 }}>{t('instructions_copied')}</span>}
                    {copyStatus === 'error' && <span style={{ color: '#dc2626', fontSize: 13 }}>{t('instructions_copy_failed')}</span>}
                  </div>
                </div>
              </div>
            )}

            {WIZARD_STEPS[wizardStep] === 'paste' && (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>{t('label_paste_csv')}</label>
                  <textarea
                    value={pastedCsvText}
                    onChange={e => setPastedCsvText(e.target.value)}
                    placeholder={t('placeholder_paste_csv')}
                    style={{ width: '100%', minHeight: 100, padding: 8, fontSize: 12, boxSizing: 'border-box', border: `1px solid ${tokens.colors.borderSecondary}`, borderRadius: 8 }}
                  />
                </div>

                {parseError && <div style={{ color: 'red', marginBottom: 12 }}>{parseError}</div>}

                <StandardButton
                  variant="action"
                  label={parsing ? t('parsing') : t('button_parse')}
                  disabled={!pastedCsvText.trim() || !selectedFreguesiaId || parsing}
                  onClick={handleParse}
                />
              </div>
            )}
          </div>
        )}

        {step === 'review' && (
          <ReviewScreen
            t={t}
            rows={rows}
            rowCounts={rowCounts}
            batchFreguesiaName={selectedFreguesiaNames[0] || ''}
            onToggleInclude={handleToggleInclude}
            onToggleRights={handleToggleRights}
            onImport={handleImport}
          />
        )}

        {step === 'importing' && (
          <div>
            <h2>{t('importing_heading')}</h2>
            <p>{t('importing_progress', { done: importProgress.done, total: importProgress.total })}</p>
          </div>
        )}

        {step === 'done' && (
          <div>
            <h2>{t('done_heading', { count: importResults.succeeded })}</h2>
            {importResults.failed.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p>{t('done_failed_heading', { count: importResults.failed.length })}</p>
                <ul>
                  {importResults.failed.map((f, i) => (
                    <li key={i}>{f.title}: {translateErrorCode(t, f.errorCode)}</li>
                  ))}
                </ul>
              </div>
            )}
            <StandardButton variant="action" label={t('button_review_and_publish')} onClick={goToPendingEvents} />
          </div>
        )}
      </div>
      <FooterContainer>
        <FooterButtonsList showGoToTop={false} />
      </FooterContainer>
    </>
  );
};

// -----------------------------------------------------------------------
// Review screen — two groups: Needs a look, Ready to import
// -----------------------------------------------------------------------
function ReviewScreen({ t, rows, rowCounts, batchFreguesiaName, onToggleInclude, onToggleRights, onImport }) {
  const formattingLocale = useFormattingLocale();
  const flaggedRows = rows.filter(r => r.flags.length > 0);
  const plainRows = rows.filter(r => r.flags.length === 0);
  const importableCount = rows.filter(r => r.includeInImport).length;

  // The override checkbox reads differently depending on what it's overriding
  // (Phase 5b §2.3): an unresolved-routing row is offered a named fallback
  // destination, everything else keeps the generic "include anyway" wording.
  const includeCheckboxLabel = (row) => {
    const isUnresolvedRouting = row.flags.some(f => f.type === 'routing_unresolved');
    return isUnresolvedRouting
      ? t('row_use_batch_freguesia', { freguesia: batchFreguesiaName })
      : t('row_include_anyway');
  };

  return (
    <div>
      <h2>{t('review_heading')}</h2>
      <p style={{ fontSize: 13, color: '#6b7280' }}>
        {t('row_count', { read: rowCounts.read, shown: rowCounts.shown })}
      </p>

      {flaggedRows.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h3>{t('section_flagged_rows')}</h3>
          {flaggedRows.map(row => (
            <div key={row.rowIndex} style={{ border: '1px solid #D1D5DB', borderRadius: 8, padding: 12, marginBottom: 8 }}>
              <strong>{row.title || t('untitled_row')}</strong>
              <ul>
                {row.flags.map((f, i) => <li key={i} style={{ color: '#B45309' }}>{t(f.messageKey, f.messageParams)}</li>)}
              </ul>
              {row.blocked ? (
                <em>{t('row_blocked_from_import')}</em>
              ) : (
                <label>
                  <input type="checkbox" checked={row.includeInImport} onChange={() => onToggleInclude(row.rowIndex)} />
                  {' '}{includeCheckboxLabel(row)}
                </label>
              )}
              {row.imageUrl && (
                <div style={{ marginTop: 8 }}>
                  <label>
                    <input type="checkbox" checked={row.rightsConfirmed} onChange={() => onToggleRights(row.rowIndex)} />
                    {' '}{t('rights_checkbox_label')}
                  </label>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      <section style={{ marginBottom: 24 }}>
        <h3>{t('section_ready_to_import')}</h3>
        {plainRows.map(row => (
          <div key={row.rowIndex} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid #eee' }}>
            <span style={{ flex: 1 }}>{row.title}</span>
            <span>{row.resolvedFreguesiaName || ''}</span>
            <span>{row.startDateTime ? row.startDateTime.toLocaleDateString(formattingLocale) : ''}</span>
            {row.imageUrl && (
              <label>
                <input type="checkbox" checked={row.rightsConfirmed} onChange={() => onToggleRights(row.rowIndex)} />
                {' '}{t('rights_checkbox_label')}
              </label>
            )}
          </div>
        ))}
      </section>

      <StandardButton
        variant="action"
        label={t('button_import', { count: importableCount })}
        disabled={importableCount === 0}
        onClick={onImport}
      />
    </div>
  );
}

export default EventsImporter;
